import {
  EltPeriodCell,
  getPreviousQuarterAndYear,
} from 'legacy/lut/components/widgets/public/partner360/ELT'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'

import { Translation } from 'cng-common/features/translations'
import { NotificationController } from 'core-components/library/popup'

import { IFinancialCockpitEltDocument } from '../../../../api/types'
import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { formatCockpitActualsDate } from '../../../../lib'
import {
  buildEltDocumentOpenLink,
  canDownloadEltPptx,
  getEltDocumentDisplayName,
  getEltFormatLabelKey,
  parseEltDocumentSettings,
} from '../../../../lib/documentsAndLinks'
import { deleteEltDocument, downloadEltDocumentPptx } from '../../../../lib/documentsAndLinks/api'
import { removeCockpitEltDocument } from '../../../../model'
import { CockpitTable, ICockpitTableColumn } from '../../../components/cockpitTable'
import { useCanDeleteCockpitEltDocument } from '../../hooks/useCanDeleteCockpitEltDocument'
import { DocumentActions } from '../documentActions/DocumentActions'

import styles from './EltDocumentsTable.module.scss'

interface IEltDocumentsTableProps {
  partnerId: string
  documents: IFinancialCockpitEltDocument[]
  readOnly: boolean
  showPartnerId?: boolean
  getPartnerId?: (document: IFinancialCockpitEltDocument) => string
}

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.documentsAndLinks

export const EltDocumentsTable = ({
  partnerId,
  documents,
  readOnly,
  getPartnerId,
  showPartnerId = false,
}: IEltDocumentsTableProps) => {
  const canDelete = useCanDeleteCockpitEltDocument()
  const dispatch = useAppDispatch()

  const columns: ICockpitTableColumn<IFinancialCockpitEltDocument>[] = [
    {
      key: showPartnerId ? 'partnerId' : 'name',
      header: (
        <Translation span text={showPartnerId ? KEYS.columns.partnerId : KEYS.columns.name} />
      ),
      renderCell: (document) =>
        showPartnerId
          ? (getPartnerId?.(document) ?? partnerId)
          : getEltDocumentDisplayName(document),
    },
    {
      key: 'period',
      header: <Translation span text={KEYS.columns.period} />,
      renderCell: (document) => {
        if (
          !document.FYear ||
          !Number.isInteger(document.FQuarter) ||
          document.FQuarter < 1 ||
          document.FQuarter > 4
        )
          return <span>—</span>
        const { year, quarter } = getPreviousQuarterAndYear(document.FYear, document.FQuarter)
        return <EltPeriodCell fYear={year} fQuarter={quarter} />
      },
    },
    {
      key: 'netNdp',
      header: <Translation span text={KEYS.columns.netNdp} />,
      renderCell: (document) => parseEltDocumentSettings(document.Settings).netNdp ?? '—',
    },
    {
      key: 'format',
      header: <Translation span text={KEYS.columns.format} />,
      renderCell: (document) => <Translation span text={getEltFormatLabelKey(document.EltType)} />,
    },
    {
      key: 'status',
      header: <Translation span text={KEYS.columns.status} />,
      renderCell: (document) => (
        <Translation
          span
          text={document.Finalized === 1 ? KEYS.status.finalized : KEYS.status.draft}
        />
      ),
    },
    {
      key: 'lastModified',
      header: <Translation span text={KEYS.columns.lastModified} />,
      renderCell: (document) =>
        document.FormModified ? formatCockpitActualsDate(document.FormModified) : '—',
    },
    {
      key: 'modifiedBy',
      header: <Translation span text={KEYS.columns.modifiedBy} />,
      renderCell: (document) => document.FormModifiedBy || '—',
    },
    {
      key: 'actions',
      header: null,
      renderCell: (document) => (
        <DocumentActions
          openHref={buildEltDocumentOpenLink(
            getPartnerId?.(document) ?? partnerId,
            document.FYear,
            document.UID
          )}
          documentLabel={getEltDocumentDisplayName(document)}
          canDownload={canDownloadEltPptx(document)}
          onDownload={() => downloadEltDocumentPptx(document)}
          readOnly={readOnly}
          canDelete={canDelete}
          onDelete={() => deleteEltDocument(document.EntryId)}
          onDeleted={() => {
            dispatch(
              removeCockpitEltDocument({
                partnerId: getPartnerId?.(document) ?? partnerId,
                entryId: document.EntryId,
              })
            )
            NotificationController.showSuccess({ message: 'ELT document deleted successfully.' })
          }}
          onDeleteError={() => {
            NotificationController.showError({ message: 'Failed to delete the ELT document.' })
          }}
        />
      ),
    },
  ]

  if (documents.length === 0) {
    return (
      <div className={styles.empty}>
        <Translation span text={KEYS.emptyElt} />
      </div>
    )
  }

  return (
    <CockpitTable
      columns={columns}
      items={documents}
      getItemKey={(document) => `${getPartnerId?.(document) ?? partnerId}:${document.EntryId}`}
    />
  )
}

EltDocumentsTable.displayName = 'EltDocumentsTable'
